// components/FeedItem.js — Bold modern redesign (Vercel/Stripe aesthetic)
// - Removed all colored left accent bars
// - Consistent px:2 py:1.75 spacing on every row
// - 3-color semantic badge system (primary / amber / green)
// - Bricolage Grotesque + DM Sans typography
// - Clean hover, no opacity theatrics
// - Campaign company name shown as top-level actor in header

import React from "react";
import { Box, Typography, IconButton, Tooltip, Chip, Avatar } from "@mui/material";
import { useTheme, alpha, keyframes } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import {
  MonetizationOn  as PayoutIcon,
  Campaign        as CampaignIcon,
  Handshake       as PartnershipIcon,
  Send            as SendIcon,
  PersonAdd       as JoinIcon,
  EmojiEvents     as AchievementIcon,
  Reply           as ReplyIcon,
  IosShare        as ShareIcon,
  Work            as WorkIcon,
  Groups          as BubbleIcon,
  Payments        as PaymentIcon,
} from "@mui/icons-material";
import LinkedAvatar    from "./LinkedAvatar";
import ActivitySummary from "./ActivitySummary";
import EnhancedReactionBar from "./reactionbar";
import MediaDisplay    from "./mediaDisplay";
import {
  getCleanDisplayName,
  isCreatorActivity,
  getCreatorIdentifier,
  getRelativeTime,
  getCampaignCompany,
} from "./activityHelpers";

/* ─────────────────────────────────────────────────────────
   Design tokens — keep in sync with ZapFeed.js
───────────────────────────────────────────────────────── */
const FONT = {
  display: "'Bricolage Grotesque', sans-serif",
  body:    "'DM Sans', sans-serif",
};

/* ─────────────────────────────────────────────────────────
   Mount animation
───────────────────────────────────────────────────────── */
const mountIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to   { opacity: 1; transform: translateY(0);   }
`;

/* ─────────────────────────────────────────────────────────
   Activity config — 3 semantic colors only
   Blue  → creator-facing (Campaign, Pitch, Partnership)
   Amber → achievement / milestone
   Green → money / work (Payout, Payment, CastingCall)
   No color → Post, Join, Reply (neutral)
───────────────────────────────────────────────────────── */
const SEMANTIC = {
  blue:  "#2563eb",
  amber: "#d97706",
  green: "#059669",
};

const ACTIVITY_CFG = {
  Post:        { color: null,          Icon: null,            label: null           },
  Campaign:    { color: SEMANTIC.blue, Icon: CampaignIcon,    label: "Campaign"     },
  Achievement: { color: SEMANTIC.amber,Icon: AchievementIcon, label: "Achievement"  },
  CastingCall: { color: SEMANTIC.green,Icon: WorkIcon,        label: "Casting Call" },
  Payout:      { color: SEMANTIC.green,Icon: PayoutIcon,      label: "Payout"       },
  Partnership: { color: SEMANTIC.blue, Icon: PartnershipIcon, label: "Partnership"  },
  Join:        { color: null,          Icon: JoinIcon,        label: "Joined"       },
  Payment:     { color: SEMANTIC.green,Icon: PaymentIcon,     label: "Payment"      },
  Pitch:       { color: SEMANTIC.blue, Icon: SendIcon,        label: "Pitch"        },
  Reply:       { color: null,          Icon: ReplyIcon,       label: "Reply"        },
};

/* ─────────────────────────────────────────────────────────
   ActivityTypeBadge
───────────────────────────────────────────────────────── */
function ActivityTypeBadge({ cfg, theme }) {
  if (!cfg?.label) return null;
  const { color, Icon, label } = cfg;
  const c = color || theme.palette.text.secondary;
  return (
    <Chip
      label={label}
      size="small"
      icon={Icon ? <Icon sx={{ fontSize: "10px !important", ml: "5px !important" }} /> : undefined}
      sx={{
        height: 20,
        fontSize: "0.67rem",
        fontWeight: 600,
        letterSpacing: "0.01em",
        fontFamily: FONT.body,
        color: c,
        bgcolor: alpha(c, 0.08),
        border: `1px solid ${alpha(c, 0.18)}`,
        "& .MuiChip-icon": { color: c },
        "& .MuiChip-label": { px: 0.75 },
        flexShrink: 0,
      }}
    />
  );
}

/* ─────────────────────────────────────────────────────────
   CastingCallStats
───────────────────────────────────────────────────────── */
function CastingCallStats({ castingCall }) {
  if (!castingCall) return null;
  const stats = [
    castingCall.budget > 0 && {
      label: `$${Number(castingCall.budget).toLocaleString()}`,
      color: SEMANTIC.green,
    },
    castingCall.applicant_count != null && {
      label: `${castingCall.applicant_count} applicant${castingCall.applicant_count === 1 ? "" : "s"}`,
      color: SEMANTIC.blue,
    },
    castingCall.call_type && {
      label: castingCall.call_type === "creator" ? "Creator Call" : "General",
      color: "#7c3aed",
    },
  ].filter(Boolean);

  if (!stats.length) return null;

  return (
    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.875}>
      {stats.map(({ label, color }) => (
        <Chip
          key={label}
          label={label}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.7rem",
            fontWeight: 600,
            fontFamily: FONT.body,
            color,
            bgcolor: alpha(color, 0.07),
            border: `1px solid ${alpha(color, 0.16)}`,
            "& .MuiChip-label": { px: 0.8 },
          }}
        />
      ))}
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────── */
export default function FeedItem({
  activity,
  onPitchClick,
  onCastingCallClick,
  onCastingCallShareClick,
  onShare,
  onReactionChange,
  userAuth,
  queryClient,
  onSnackbarMessage,
  isCreator,
}) {
  const theme = useTheme();

  const {
    activity_type,
    is_private,
    media_url,
    media_type,
    bubble_name,
    is_global = true,
    activity_date,
    user_id,
    casting_call,
  } = activity;

  const cfg = ACTIVITY_CFG[activity_type] || ACTIVITY_CFG.Post;

  const isPost        = activity_type === "Post";
  const isAchievement = activity_type === "Achievement";
  const isCastingCall = activity_type === "CastingCall";
  const canReact      = isPost || isAchievement;
  const canThread     = canReact && (userAuth.userOK || userAuth.creatorOK);
  const hasMedia      = !!(media_url && media_type);
  const hasPitch      = isCreator &&
    ["Campaign", "Partnership", "Pitch"].includes(activity_type) && !is_private;

  // ── Display name resolution ────────────────────────────
  // For campaigns with a resolved company, show the company as the actor.
  // Everything else falls through to the normal individual name logic.
  const campaignCompany   = activity_type === "Campaign" ? getCampaignCompany(activity) : null;
  const displayName       = campaignCompany?.name || getCleanDisplayName(activity);

  const relTime           = getRelativeTime(activity_date);
  const isCreatorAct      = isCreatorActivity(activity);
  const creatorIdentifier = isCreatorAct ? getCreatorIdentifier(activity) : null;

  // Campaign companies don't have a profile page, so suppress the link when
  // the company is the displayed actor.
  const profileLink = is_private || campaignCompany
    ? null
    : creatorIdentifier
      ? `/profile/${creatorIdentifier}`
      : user_id
        ? `/profile/${user_id}`
        : null;

  return (
    <Box
      onClick={isCastingCall ? () => onCastingCallClick?.(activity) : undefined}
      sx={{
        position: "relative",
        display: "flex",
        gap: 1.5,
        px: 2,
        py: 1.75,
        cursor: isCastingCall ? "pointer" : "default",
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        animation: `${mountIn} 0.22s ease both`,
        transition: "background 0.12s ease",

        "&:hover": {
          "& .feed-actions": { opacity: 1 },
        },

        ...(is_private && { opacity: 0.72 }),
      }}
    >
      {/* ── Avatar ──────────────────────────────────────── */}
      <Box sx={{ flexShrink: 0, pt: 0.1 }}>
        {/* If we have a campaign company photo, show it instead of the user avatar */}
        {campaignCompany?.photo ? (
          <Avatar
            src={campaignCompany.photo}
            alt={campaignCompany.name}
            sx={{
              width: 40, height: 40,
              border: `1.5px solid ${alpha(theme.palette.divider, 0.6)}`,
              transition: "transform 0.15s ease",
              "&:hover": { transform: "scale(1.03)" },
            }}
          />
        ) : (
          <LinkedAvatar
            activity={activity}
            sx={{
              width: 40, height: 40,
              transition: "transform 0.15s ease",
              "&:hover": { transform: "scale(1.03)" },
            }}
          />
        )}
      </Box>

      {/* ── Right column ──────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>

        {/* Header row */}
        <Box
          display="flex" alignItems="flex-start"
          justifyContent="space-between"
          gap={0.5}
          sx={{ mb: 0.25 }}
        >
          {/* Left meta */}
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5} sx={{ minWidth: 0 }}>

            {/* Display name — company name or individual */}
            <Typography
              component={profileLink ? RouterLink : "span"}
              to={profileLink ?? undefined}
              sx={{
                fontWeight: 700,
                fontSize: "0.9rem",
                fontFamily: FONT.body,
                color: "text.primary",
                textDecoration: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: { xs: 120, sm: 200 },
                lineHeight: 1.3,
                letterSpacing: "-0.01em",
                "&:hover": profileLink ? { textDecoration: "underline" } : {},
              }}
            >
              {displayName}
            </Typography>

            {/* Activity badge */}
            <ActivityTypeBadge cfg={cfg} theme={theme} />

            {/* Time */}
            <Typography sx={{
              fontSize: "0.8rem",
              color: "text.disabled",
              fontFamily: FONT.body,
              flexShrink: 0,
              lineHeight: 1.3,
              fontWeight: 400,
            }}>
              · {relTime}
            </Typography>

            {/* Bubble chip */}
            {!is_global && bubble_name && (
              <Chip
                icon={<BubbleIcon sx={{ fontSize: "10px !important", ml: "5px !important" }} />}
                label={bubble_name}
                size="small"
                sx={{
                  height: 20, fontSize: "0.67rem", fontWeight: 600,
                  fontFamily: FONT.body,
                  bgcolor: alpha(theme.palette.primary.main, 0.07),
                  color: theme.palette.primary.main,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                  "& .MuiChip-icon": { color: theme.palette.primary.main },
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            )}
          </Box>

          {/* Right actions — shown at low opacity, full on hover */}
          <Box
            className="feed-actions"
            display="flex" alignItems="center" gap={0.1}
            sx={{
              flexShrink: 0,
              opacity: 0.25,
              transition: "opacity 0.15s ease",
            }}
          >
            {hasPitch && (
              <Tooltip title="Pitch" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onPitchClick?.(activity); }}
                  sx={{
                    p: 0.625, borderRadius: "6px",
                    color: "text.secondary",
                    "&:hover": {
                      color: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.07),
                    },
                    transition: "all 0.12s",
                  }}
                >
                  <SendIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}

            {(canThread || isCastingCall) && (
              <Tooltip title="Share" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    isCastingCall
                      ? onCastingCallShareClick?.(e, activity)
                      : onShare?.(activity);
                  }}
                  sx={{
                    p: 0.625, borderRadius: "6px",
                    color: "text.secondary",
                    "&:hover": {
                      color: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.07),
                    },
                    transition: "all 0.12s",
                  }}
                >
                  <ShareIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Activity body */}
        <ActivitySummary activity={activity} showHeader={false} showDate={false} />

        {/* CastingCall stats */}
        {isCastingCall && <CastingCallStats castingCall={casting_call} />}

        {/* Media */}
        {hasMedia && (
          <Box sx={{
            mt: 1.25, borderRadius: "10px",
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}>
            <MediaDisplay
              mediaUrl={media_url}
              mediaType={media_type}
              mediaWidth={activity.media_width}
              mediaHeight={activity.media_height}
              alt={`Media from ${displayName}`}
            />
          </Box>
        )}

        {/* Reaction bar */}
        {canReact && (
          <Box sx={{ mt: 0.5 }}>
            <EnhancedReactionBar
              activity={activity}
              onReactionChange={(data) => onReactionChange?.({ ...data, activity })}
              userAuth={userAuth}
              queryClient={queryClient}
              onSnackbarMessage={onSnackbarMessage}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}